import React from "react";
import { useDrop } from "react-dnd";
import { toast } from "react-toastify";
import StudentCell from "./StudentCell.js";
import { groupStudentsByCoach, findCoachByIdOrName } from "./utils.js";

const DroppableCell = ({
  day,
  hour,
  studentsInCell,
  coach,
  coaches,
  isWeekView,
  isAvailable = true,
  moveStudent,
  setAddingStudent,
  setDisplayedStudentName,
  setSelectedStudentToAdd,
  onManageStudent,
  columnIndex = 0,
  totalColumns = 1,
}) => {
  const [, drop] = useDrop(() => ({
    accept: "STUDENT",
    canDrop: () => isAvailable,
    drop: (item) => {
      if (!isAvailable) {
        toast.warning(`Trainer ist nicht verfügbar am ${day} um ${hour}:00 Uhr`);
        return;
      }

      // In WeekView, preserve student's existing coach (pass the coach from the drag item)
      // In DayView, use the coach from the column
      let targetCoach = coach ? coach._id : (item.coach || undefined);

      // Pass the original day/hour so backend can update only that specific assignment
      moveStudent(item.id, day, hour, targetCoach, item.fromDay, item.fromHour);
    },
  }), [isAvailable, day, hour, coach, moveStudent]);

  // Check for mismatched skill levels (adults) OR training groups (children)
  const hasDifferentSkillLevels =
    studentsInCell.length > 0 &&
    (new Set(studentsInCell.map((student) => student.skillLevel)).size > 1 ||
     new Set(studentsInCell.map((student) => student.trainigGroup)).size > 1);

  console.log("studentsInCell:", studentsInCell);
  console.log("hasDifferentSkillLevels:", hasDifferentSkillLevels);

  // Click handler for opening AddStudentDialog
  const handleCellClick = (e) => {
    // Only open dialog if clicking directly on the cell (not on a student)
    if (e.target.classList.contains('droppable-cell')) {
      if (!isAvailable) {
        toast.warning(`Trainer ist nicht verfügbar am ${day} um ${hour}:00 Uhr`);
        return;
      }
      setAddingStudent({ day, hour, coach: coach ? coach._id : null });
      setDisplayedStudentName("");
      setSelectedStudentToAdd("");
    }
  };

  // Week view: show ALL available coaches as drop zones
  if (isWeekView) {
    // Find all coaches available at this day/hour
    const availableCoaches = coaches.filter(c =>
      Array.isArray(c.availableTimes) &&
      c.availableTimes.some(time => String(time).trim() === `${day} ${hour}`)
    );

    // Group existing students by coach
    const studentsByCoach = groupStudentsByCoach(studentsInCell, day, hour);

    return (
      <div
        className={`droppable-cell ${
          hasDifferentSkillLevels ? "cell-warning" : ""
        } ${!isAvailable ? "cell-unavailable" : ""}`}
        onClick={handleCellClick}
        title={!isAvailable ? "Trainer nicht verfügbar" : ""}
      >
        {availableCoaches.map((coachObj) => {
          // Get students for this coach (or empty array if none)
          const coachStudents = studentsByCoach[coachObj._id] || [];
          const displayName = coachObj.firstName;

          // Create a drop zone for THIS specific coach
          const CoachGroupDropZone = () => {
            const [, groupDrop] = useDrop(() => ({
              accept: "STUDENT",
              canDrop: () => isAvailable,
              drop: (item) => {
                if (!isAvailable) {
                  toast.warning(`Trainer ist nicht verfügbar am ${day} um ${hour}:00 Uhr`);
                  return;
                }
                // Assign to this coach - pass fromDay/fromHour to preserve other assignments
                moveStudent(item.id, day, hour, coachObj._id, item.fromDay, item.fromHour);
              },
            }), []);

            // Handle click to add student to this specific coach
            const handleCoachGroupClick = (e) => {
              // Only handle left-click (not right-click for context menu)
              if (e.button !== 0) return;

              // Don't interfere with dragging or clicking on students
              const target = e.target;
              const isDraggableStudent = target.closest('.draggable-student');

              // Only open dialog if clicking on coach header or empty area (not on student)
              if (!isDraggableStudent) {
                e.stopPropagation(); // Prevent bubbling to parent cell
                if (!isAvailable) {
                  toast.warning(`Trainer ist nicht verfügbar am ${day} um ${hour}:00 Uhr`);
                  return;
                }
                setAddingStudent({ day, hour, coach: coachObj._id });
                setDisplayedStudentName("");
                setSelectedStudentToAdd("");
              }
            };

            return (
              <div
                ref={groupDrop}
                className="week-coach-group"
                onClick={handleCoachGroupClick}
                style={{ cursor: 'pointer' }}
              >
                <div className="week-coach-header">👨‍🏫 {displayName}</div>
                <div className="week-coach-students">
                  {coachStudents.map((student) => (
                    <StudentCell
                      key={student._id}
                      student={student}
                      day={day}
                      hour={hour}
                      onManageStudent={onManageStudent}
                      columnIndex={columnIndex}
                      totalColumns={totalColumns}
                    />
                  ))}
                </div>
              </div>
            );
          };

          return <CoachGroupDropZone key={coachObj._id} />;
        })}
      </div>
    );
  }

  // Day view: normal rendering
  return (
    <div
      ref={drop}
      className={`droppable-cell ${
        hasDifferentSkillLevels ? "cell-warning" : ""
      } ${!isAvailable ? "cell-unavailable" : ""}`}
      onClick={handleCellClick}
      title={!isAvailable ? "Trainer nicht verfügbar" : ""}
    >
      {studentsInCell.map((student) => (
        <StudentCell
          key={student._id}
          student={student}
          day={day}
          hour={hour}
          onManageStudent={onManageStudent}
          columnIndex={columnIndex}
          totalColumns={totalColumns}
        />
      ))}
    </div>
  );
};

export default DroppableCell;
